import { Shuffle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHydrated } from "@/hooks/useHydrated";
import { Input } from "@/components/ui/input";
import { DISPLAY_NAME_MAX_LENGTH } from "@/engine/state/displayName";
import {
  randomizeAvatarChoice,
  resolveAvatarColor,
  resolveAvatarEmoji,
} from "@/engine/state/avatarChoice";
import { setAvatarChoice, useAvatarChoice } from "@/hooks/useAvatarChoice";
import {
  setDisplayName,
  setSuggestedDisplayName,
  useDisplayName,
} from "@/hooks/useDisplayName";
import { suggestNickname } from "@/engine/state/nickname";
import {
  campIdentityFor,
  choiceForCamp,
  randomCampIdentity,
} from "@/components/room/campIdentity";
import { getSessionPeerId } from "@/engine/state/roomIdentity";

interface AvatarIdentityProps {
  /** Used to preview the deterministic fallback when nothing is picked yet. */
  peerId?: string;
  /** Focus the name field on mount, useful on the entry screen. */
  autoFocus?: boolean;
  /** Render the name as plain text instead of an editable input. */
  readOnlyName?: boolean;
  /**
   * Make the avatar itself the shuffle, and keep the labelled button for wide
   * screens only. For the entry screen, where the row has a name field to feed
   * and a phone has no width to spare for a word beside an icon beside a face.
   */
  shufflesOnAvatar?: boolean;
  /**
   * The character is drawn beside this row as a camp animal (the entry
   * screen's card), so the row draws no face of its own and shuffles within
   * the animals the library draws.
   */
  campOnly?: boolean;
}

/**
 * The shared identity block: avatar preview, display name, and randomize.
 * Rendered once above settings sections so it does not remount on tab switch.
 */
export function AvatarIdentity({
  peerId = "",
  autoFocus,
  readOnlyName,
  shufflesOnAvatar,
  campOnly = false,
}: AvatarIdentityProps) {
  const choice = useAvatarChoice();
  const { t } = useTranslation();
  const displayName = useDisplayName();
  const hydrated = useHydrated();
  const effectivePeerId = peerId || getSessionPeerId();
  const previewEmoji = resolveAvatarEmoji(effectivePeerId, choice.emoji);
  const previewColor = resolveAvatarColor(effectivePeerId, choice.color);

  const handleRandomize = () => {
    const next = campOnly
      ? choiceForCamp(
          randomCampIdentity(campIdentityFor(effectivePeerId, choice)),
        )
      : randomizeAvatarChoice(choice);
    setSuggestedDisplayName(suggestNickname(effectivePeerId, next));
    setAvatarChoice(next);
  };

  if (!hydrated) {
    return (
      <div className="flex items-center gap-3" data-testid="avatar-loading">
        <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-muted" />
        <div className="min-w-0 flex-1">
          <div className="h-9 w-full animate-pulse rounded-md bg-muted" />
        </div>
        <div className="ml-auto h-6 w-20 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  const avatarStyle = {
    backgroundColor: `${previewColor}33`,
    border: `2px solid ${previewColor}`,
  };
  const avatarClass =
    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl";

  return (
    <div className="flex items-center gap-3">
      {campOnly ? null : shufflesOnAvatar ? (
        /* The face is the control. The emoji stays out of the accessibility
           tree — what a screen reader is offered is the action, not the
           picture, which is the same rule the labelled button already follows.

           On a wide screen that makes two controls with one name, because the
           labelled button is still there. That is a duplicate affordance rather
           than an oversight: the phone needs the width and the desktop needs
           the word, and the alternative is branching on a width the server
           cannot know. */
        <button
          type="button"
          onClick={handleRandomize}
          aria-label={t("avatar.randomize")}
          data-testid="avatar-shuffle-face"
          className={`${avatarClass} transition-transform hover:scale-105 active:scale-95`}
          style={avatarStyle}
        >
          <span aria-hidden>{previewEmoji}</span>
        </button>
      ) : (
        <div className={avatarClass} style={avatarStyle} aria-hidden>
          {previewEmoji}
        </div>
      )}
      {readOnlyName ? (
        <div className="min-w-0 flex-1 flex items-center">
          <span
            data-testid="avatar-readonly-name"
            className="truncate text-sm font-medium"
          >
            {displayName || t("avatar.namePlaceholder")}
          </span>
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <Input
            id="display-name"
            data-testid="entry-name"
            aria-label={t("avatar.yourName")}
            value={displayName}
            maxLength={DISPLAY_NAME_MAX_LENGTH}
            placeholder={t("avatar.namePlaceholder")}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus={autoFocus}
          />
        </div>
      )}

      <button
        type="button"
        onClick={handleRandomize}
        aria-label={t("avatar.randomize")}
        data-testid="avatar-randomize"
        className={`ml-auto ${shufflesOnAvatar && !campOnly ? "hidden sm:flex" : "flex"} items-center gap-1.5 rounded-md border border-border/60 px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground`}
      >
        <Shuffle className="h-3.5 w-3.5" />
        {t("avatar.random")}
      </button>
    </div>
  );
}
